import moment from 'moment';
import {
  LeadConversionReport,
  FollowUpStatusReport,
  LeadConversionSOBReport,
  LeadConversionCrmagentsReport,
  LeadConversionSalespersonReport,
  LeadConversionVehicleReport,
  BDCReport,
  DailyLeadManagementReport,
  LeadConversionITReport,
} from '../../../containers/reports/components/report-utils';
import { Dict } from '../../models';
import { DateFormats, formatDate } from '../../utils';
import { ApiResponse, Lead, ListSort, PaginatedApiResponse } from '../models';
import {
  ActivityDateWiseLineReport,
  IActivitySummaryReport,
  ICallactivitySummary,
  IDailySalesVehicleGroupedReport,
  IShowroomPaymentCollection,
  ISummaryData,
  IVehicleActivityReportItem,
  Showroom1,
} from '../models/report-models';
import { WhatappReportData } from '../models/wappRequests';
import * as http from './http';
import ApiService from './service';

export class ReportService extends ApiService {
  async getLeadConversions(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions`;
    const response = await http.post<ApiResponse<LeadConversionReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }
  async getDailyLeadReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
    createdBy?: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/daily`;
    const response = await http.post<ApiResponse<DailyLeadManagementReport>>(
      url,
      data,
      this.store
    );

    return response.data;
  }

  async getDailyLeadReportDownload(data: {
    fromDate?: Date;
    toDate?: Date;
    groupBy?: string;
    createdBy?: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/daily/download`;
    const response = await http.downloadPostData(url, data, this.store);

    http.saveDownloadedFile(
      response.data,
      'application/application/vnd.openxmlformats',
      `Daily-Lead-Management-Report-${new Date()}.xlsx`
    );

    // return response.data;
  }

  async getLeadConversionSOB(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/sob`;
    const response = await http.post<ApiResponse<LeadConversionSOBReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getLeadConversionIT(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/it`;
    const response = await http.post<ApiResponse<LeadConversionITReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getLeadConversionCrmagent(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/crmagent`;
    const response = await http.post<
      ApiResponse<LeadConversionCrmagentsReport>
    >(url, data, this.store);
    return response.data;
  }

  async getLeadConversionSalesperson(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/salesperson`;
    const response = await http.post<
      ApiResponse<LeadConversionSalespersonReport>
    >(url, data, this.store);
    return response.data;
  }

  async getLeadConversionVehicle(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/lead-conversions/car-line`;
    const response = await http.post<ApiResponse<LeadConversionVehicleReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getPaymentReports(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/payments`;
    const response = await http.post<ApiResponse<IShowroomPaymentCollection[]>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getActivitySummarySubReport(
    data: {
      fromDate: Date;
      toDate: Date;
      groupBy: string;
    },
    apiEndpoint: string
  ) {
    const url = `${this.apiDomain}${apiEndpoint}`;
    const response = await http.post<ApiResponse<ICallactivitySummary>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getDailySalesReportGroupedByVehicle(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }): Promise<ApiResponse<IDailySalesVehicleGroupedReport>> {
    const url = `${this.apiDomain}/api/reports/sales-report/vehicle`;
    const response = await http.post<
      ApiResponse<IDailySalesVehicleGroupedReport>
    >(url, data, this.store);
    return response.data;
  }

  async getVehicleActivityReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/vehicle-activities`;
    const response = await http.post<ApiResponse<IActivitySummaryReport>>(
      url,
      data,
      this.store
    );

    return response.data;
  }

  async getVehicleLineActivityReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/vehicle-activities-date`;
    const response = await http.post<ApiResponse<ActivityDateWiseLineReport>>(
      url,
      data,
      this.store
    );

    return response.data;
  }

  async getStaffActivityReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/activities`;
    const response = await http.post<ApiResponse<IActivitySummaryReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getActivityReportByType(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/activity/report-type`;
    const response = await http.post<ApiResponse<IActivitySummaryReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getActivitySummaryReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/activity/report-summary`;
    const response = await http.post<ApiResponse<IActivitySummaryReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getStaffActivityLineReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/activities-date`;
    const response = await http.post<ApiResponse<ActivityDateWiseLineReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async getConsolidatedActivityReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/consolidated-activities`;
    const response = await http.post<ApiResponse<{ set1: ISummaryData }>>(
      url,
      data,
      this.store
    );

    return response.data;
  }

  async getConsolidatedVehicleActivityReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/consolidated-vehicle-activities`;
    const response = await http.post<ApiResponse<{ set1: ISummaryData }>>(
      url,
      data,
      this.store
    );

    return response.data;
  }

  async getFollowUpStatusReport(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/new-lead-conversions/followup`;
    const response = await http.post<ApiResponse<FollowUpStatusReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }
  async getBDCReport(data: { fromDate?: Date; toDate?: Date }) {
    const url = `${this.apiDomain}/api/reports/bdc`;
    const response = await http.post<ApiResponse<BDCReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  public async downloadCampaigns(data: { date: Date }): Promise<void> {
    const url = `${this.apiDomain}/api/reports/download/campaign-report`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Campaigns-${new Date()}.xlsx`
    );
  }

  async getNewLeadConversions(data: {
    fromDate: Date;
    toDate: Date;
    groupBy: string;
  }) {
    const url = `${this.apiDomain}/api/reports/new-lead-conversions`;
    const response = await http.post<ApiResponse<LeadConversionReport>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  // get wapp requests report
  async getWappRequestReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<WhatappReportData[]>> {
    const url = `${this.apiDomain}/api/reports/get-wapp-requests`;
    const response = await http.post<PaginatedApiResponse<WhatappReportData[]>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async downloadWhatsappReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/reports/get-wapp-requests/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `WhatsappReport-${new Date()}.xlsx`
    );
  }

  public async downloadLeadSummary(data: {
    fromDate: Date;
    toDate: Date;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/lead/download/lead-report`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Campaigns-${new Date()}.xlsx`
    );
  }
  public async downloadVinReport(data: {
    fromDate: Date;
    toDate: Date;
  }): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/download/no-vin-report`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Campaigns-${new Date()}.xlsx`
    );
  }

  public async downloadInvoiceTransaction(
    franchiseName?: string
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/download/invoice-transaction`;

    const resposne = await http.downloadPostData(url, {}, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `${franchiseName}.xlsx`
    );
  }
  public async downloadDeliveryReport(franchiseName?: string): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/download/delivery-report`;

    const resposne = await http.downloadPostData(url, {}, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `${franchiseName}.xlsx`
    );
  }

  public async downloadPrevinNoFinanceReport(
    franchiseName?: string
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/download/previn-no-finance`;

    const resposne = await http.downloadPostData(url, {}, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `${franchiseName}.xlsx`
    );
  }
}
